'use client';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Link from '@/components/ui/Link';

interface Category {
    image: string;
    alt: string;
    title: string;
    subtitle: string;
    count: string;
    href: string;
    span?: string;
}

const categories: Category[] = [
    {
        image: '/images/img_rectangle_7.webp',
        alt: 'Men fashion collection with modern casual wear',
        title: 'Men',
        subtitle: 'Casual, Formal & Sport',
        count: '240+ items',
        href: '/shop',
        span: 'lg:col-span-2 lg:row-span-2',
    },
    {
        image: '/images/img_rectangle_8.webp',
        alt: 'Women fashion collection with elegant dresses',
        title: 'Women',
        subtitle: 'Dresses, Tops & More',
        count: '380+ items',
        href: '/shop',
        span: '',
    },
    {
        image: '/images/img_rectangle_9.webp',
        alt: 'Kids colorful and playful clothing collection',
        title: 'Kids',
        subtitle: 'Playful & Comfortable',
        count: '120+ items',
        href: '/shop',
        span: '',
    },
    {
        image: '/images/img_rectangle_10.webp',
        alt: 'Shoes collection including sneakers and formal footwear',
        title: 'Shoes',
        subtitle: 'Sneakers, Heels & Boots',
        count: '160+ items',
        href: '/shop',
        span: '',
    },
    {
        image: '/images/img_rectangle_11.webp',
        alt: 'Designer bags and handbags collection',
        title: 'Bags',
        subtitle: 'Handbags, Totes & Clutches',
        count: '90+ items',
        href: '/shop',
        span: '',
    },
];

const featuredCollections = [
    { title: 'Summer 2026', description: 'Light fabrics, bold colors — made for the heat.', image: '/images/img_rectangle_3.webp', alt: 'Summer 2026 collection with bright summer outfits', imgClass: 'object-cover object-[center_top]' },
    { title: 'New Collection', description: 'Fresh arrivals straight from the runway.', image: '/images/img_rectangle_92.webp', alt: 'New collection runway-inspired fashion pieces', imgClass: 'object-cover object-center' },
    { title: 'Sale Picks', description: 'Up to 50% off on selected styles.', image: '/images/img_image_6.webp', alt: 'Sale collection with discounted fashion items', imgClass: 'object-cover object-[center_top]' },
];

export default function CategoriesPage() {
    return (
        <>
            <Header />
            <main className="w-full bg-background-tertiary min-h-screen">
                {/* Page Banner */}
                <div className="w-full bg-background-secondary py-10 sm:py-14 lg:py-[60px]">
                    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 text-sm text-text-opacity-60">
                                <Link href="/" ariaLabel="Home" className="hover:text-primary-brand transition-colors">Home</Link>
                                <span>/</span>
                                <span className="text-text-primary font-medium">Categories</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-8xl text-text-primary font-semibold leading-tight">
                                Shop By Category
                            </h1>
                            <p className="text-lg sm:text-xl text-text-opacity-60 font-normal mt-1">
                                Find exactly what you're looking for
                            </p>
                        </div>
                    </div>
                </div>

                {/* Bento Category Grid */}
                <section className="w-full py-12 sm:py-16 lg:py-[80px]">
                    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 auto-rows-[280px] sm:auto-rows-[320px] lg:auto-rows-[300px]">
                            {/* Men - large card */}
                            <Link href={categories[0].href} ariaLabel={categories[0].title} className={`group relative overflow-hidden rounded-2xl lg:rounded-[24px] col-span-2 row-span-2`}>
                                <img
                                    src={categories[0].image}
                                    alt={categories[0].alt}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover object-[center_top] transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-6 sm:p-8">
                                    <span className="inline-block bg-primary-accent text-white text-xs font-semibold px-3 py-1 rounded-full mb-3">{categories[0].count}</span>
                                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">{categories[0].title}</h2>
                                    <p className="text-white/80 text-sm sm:text-base mt-1">{categories[0].subtitle}</p>
                                </div>
                            </Link>

                            {/* Women */}
                            <Link href={categories[1].href} ariaLabel={categories[1].title} className="group relative overflow-hidden rounded-2xl lg:rounded-[24px]">
                                <img src={categories[1].image} alt={categories[1].alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-[center_top] transition-transform duration-500 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-4 sm:p-5">
                                    <span className="inline-block bg-primary-accent text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">{categories[1].count}</span>
                                    <h2 className="text-xl sm:text-2xl font-bold text-white">{categories[1].title}</h2>
                                    <p className="text-white/80 text-xs sm:text-sm">{categories[1].subtitle}</p>
                                </div>
                            </Link>

                            {/* Kids */}
                            <Link href={categories[2].href} ariaLabel={categories[2].title} className="group relative overflow-hidden rounded-2xl lg:rounded-[24px]">
                                <img src={categories[2].image} alt={categories[2].alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-[center_top] transition-transform duration-500 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-4 sm:p-5">
                                    <span className="inline-block bg-primary-accent text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">{categories[2].count}</span>
                                    <h2 className="text-xl sm:text-2xl font-bold text-white">{categories[2].title}</h2>
                                    <p className="text-white/80 text-xs sm:text-sm">{categories[2].subtitle}</p>
                                </div>
                            </Link>

                            {/* Shoes */}
                            <Link href={categories[3].href} ariaLabel={categories[3].title} className="group relative overflow-hidden rounded-2xl lg:rounded-[24px]">
                                <img src={categories[3].image} alt={categories[3].alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-4 sm:p-5">
                                    <span className="inline-block bg-primary-accent text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">{categories[3].count}</span>
                                    <h2 className="text-xl sm:text-2xl font-bold text-white">{categories[3].title}</h2>
                                    <p className="text-white/80 text-xs sm:text-sm">{categories[3].subtitle}</p>
                                </div>
                            </Link>

                            {/* Bags */}
                            <Link href={categories[4].href} ariaLabel={categories[4].title} className="group relative overflow-hidden rounded-2xl lg:rounded-[24px]">
                                <img src={categories[4].image} alt={categories[4].alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-4 sm:p-5">
                                    <span className="inline-block bg-primary-accent text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">{categories[4].count}</span>
                                    <h2 className="text-xl sm:text-2xl font-bold text-white">{categories[4].title}</h2>
                                    <p className="text-white/80 text-xs sm:text-sm">{categories[4].subtitle}</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Featured Collections */}
                <section className="w-full pb-12 sm:pb-16 lg:pb-[80px]">
                    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                        <h2 className="text-3xl sm:text-4xl lg:text-7xl text-text-primary font-semibold mb-6 sm:mb-8 lg:mb-10">
                            Featured Collections
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
                            {featuredCollections.map((col, i) => (
                                <Link key={i} href="/shop" ariaLabel={col.title} className="group relative overflow-hidden rounded-2xl h-[240px] sm:h-[280px] lg:h-[320px]">
                                    <img src={col.image} alt={col.alt} className={`w-full h-full ${col.imgClass} transition-transform duration-500 group-hover:scale-105`} />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                    <div className="absolute bottom-0 left-0 p-5 sm:p-6">
                                        <h3 className="text-xl sm:text-2xl font-bold text-white">{col.title}</h3>
                                        <p className="text-white/80 text-sm mt-1">{col.description}</p>
                                        <span className="inline-flex items-center gap-1 mt-3 text-primary-accent text-sm font-semibold group-hover:gap-2 transition-all duration-200">
                                            Shop Now
                                            <img src="/images/img_arrowright.svg" alt="" className="w-4 h-4" />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
